import React, { useState, useEffect } from 'react';
import styles from './SearchFilter.module.css';

/**
 * Reusable search filter component with debouncing
 */
interface SearchFilterProps {
  /** Current search value */
  value?: string;
  /** Callback when search value changes */
  onChange?: (value: string) => void;
  /** Placeholder text for input */
  placeholder?: string;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const SearchFilter = ({
  value = '',
  onChange = () => {},
  placeholder = 'Search...',
  debounceMs = 500,
  disabled = false,
  className = '',
}: SearchFilterProps) => {
  const [localValue, setLocalValue] = useState<string>(value);

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [localValue, debounceMs, onChange, value]);

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <div className={`${styles['search-filter']} ${className}`}>
      <div className={styles['search-input-container']}>
        <input
          type="text"
          value={localValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setLocalValue(e.target.value)
          }
          placeholder={placeholder}
          disabled={disabled}
          className={styles['search-input']}
        />
        {localValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={styles['search-clear-btn']}
            title="Clear search"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchFilter;
